// YoutubeSlide — Player usando a YouTube IFrame Player API oficial.
//
// O YT.Player é criado UMA VEZ (no mount) e reutilizado via loadVideoById()
// quando o vídeo muda. Isso elimina a criação/destruição de iframes a cada
// troca, evitando o flood de erros de postMessage cross-origin gerados pela
// API do YouTube ao destruir iframes com timers pendentes.

import { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import { carregarYouTubeAPI, type YTPlayerEvent, type YTPlayerInstance } from '@/shared/lib/youtubeIframeApi';

const MARGEM_S = 4;

/** Desliga legendas/CC — cc_load_policy=0 respeita preferência do usuário; unloadModule força off. */
function desativarLegendas(player: YTPlayerInstance | null) {
  if (!player) return;
  try {
    player.unloadModule?.('captions');
    player.unloadModule?.('cc');
    player.setOption?.('captions', 'track', {});
    player.setOption?.('cc', 'track', {});
  } catch { /* ignore */ }
}

export interface YoutubeSlideHandle {
  play():   void;
  pause():  void;
  mute():   void;
  unMute(): void;
  setVolume(volume: number): void;
  /**
   * Força o player a republicar o vídeo atual a partir do segundo onde parou.
   * Chamado pelo Player após reconexão de rede para sair do spinner infinito
   * causado por XHRs internos do YouTube que quebraram durante a queda.
   */
  recoverFromNetworkDrop(): void;
  /**
   * Republica o vídeo no segundo atual (live: recarrega na ponta, sem seek).
   * `loadVideoById` reconstrói o chrome do embed — evita o ytp-bezel de pause
   * preso após um `pauseVideo()` longo (ex.: overlay de aniversário).
   */
  reloadAtCurrentTime(): void;
}

interface Props {
  videoId:          string;
  titulo:           string;
  /**
   * Identidade da reprodução atual (id do pedido ou id do item da playlist + nonce).
   * Quando muda com o mesmo videoId, o vídeo é recarregado do zero — senão o
   * player ficaria preso em ENDED (endedCalledRef nunca reseta). Mudar só o
   * índice do mesmo item (reload após aprovar sugestão) não muda esta chave.
   */
  playKey?:         string;
  duracaoSegundos?: number | null;
  /** Live streams: sem timer de duração; avanço só por ENDED real ou erro */
  modoLive?:        boolean;
  onEnded?:         () => void;
  onVideoError?:    (code: number) => void;
  unlocked?:        boolean;
  onUnlock?:        () => void;
  initialVolume?:   number;
  initialMuted?:    boolean;
}

const YoutubeSlide = forwardRef<YoutubeSlideHandle, Props>(function YoutubeSlide(
  { videoId, titulo, playKey, duracaoSegundos, modoLive = false, onEnded, onVideoError,
    unlocked = false, onUnlock, initialVolume = 100, initialMuted = false },
  ref,
) {
  // ── Refs ──────────────────────────────────────────────────────────────────
  const containerRef       = useRef<HTMLDivElement>(null);
  const playerRef          = useRef<YTPlayerInstance | null>(null);
  const playerReadyRef     = useRef(false);
  const destroyedRef       = useRef(false);
  const hasPlayedOnceRef   = useRef(false);
  const duracaoBrutaRef    = useRef(8 * 60);
  const videoIdRef         = useRef(videoId);
  const isFirstVideoRef    = useRef(true);
  const onEndedRef         = useRef(onEnded);
  const onVideoErrorRef    = useRef(onVideoError);
  const endedCalledRef     = useRef(false);
  const unlockedRef        = useRef(unlocked);
  const initialVolumeRef   = useRef(initialVolume);
  const initialMutedRef    = useRef(initialMuted);
  const modoLiveRef          = useRef(modoLive);
  const fullscreenGraceRef = useRef(false);
  const fullscreenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timerRef           = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bufWatchdogRef     = useRef<ReturnType<typeof setTimeout> | null>(null);
  const acumuladoRef       = useRef(0);
  const playStartRef       = useRef<number | null>(null);
  // Último pause veio da API (remoto, HDMI, Momento Especial) e ainda não foi retomado.
  const pausadoPorApiRef   = useRef(false);

  // ── Sync props → refs ─────────────────────────────────────────────────────
  useEffect(() => { videoIdRef.current       = videoId;       }, [videoId]);
  useEffect(() => { onEndedRef.current       = onEnded;       }, [onEnded]);
  useEffect(() => { onVideoErrorRef.current  = onVideoError;  }, [onVideoError]);
  useEffect(() => { unlockedRef.current      = unlocked;      }, [unlocked]);
  useEffect(() => { initialVolumeRef.current = initialVolume; }, [initialVolume]);
  useEffect(() => { initialMutedRef.current  = initialMuted;  }, [initialMuted]);
  useEffect(() => { modoLiveRef.current      = modoLive;      }, [modoLive]);
  useEffect(() => {
    if (modoLiveRef.current) return;
    duracaoBrutaRef.current =
      typeof duracaoSegundos === 'number' && duracaoSegundos > 0
        ? duracaoSegundos
        : 8 * 60;
  }, [duracaoSegundos]);

  // ── Funções estáveis (lêem apenas refs) ───────────────────────────────────

  function limparTimer() {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  }

  function limparBufWatchdog() {
    if (bufWatchdogRef.current) { clearTimeout(bufWatchdogRef.current); bufWatchdogRef.current = null; }
  }

  function dispararFim() {
    if (endedCalledRef.current || destroyedRef.current) return;
    endedCalledRef.current = true;
    limparBufWatchdog();
    limparTimer();
    onEndedRef.current?.();
  }

  function iniciarTimerPreciso() {
    if (modoLiveRef.current) return;
    limparTimer();
    const restante = duracaoBrutaRef.current - acumuladoRef.current;
    if (restante <= 0) { dispararFim(); return; }
    timerRef.current = setTimeout(dispararFim, (restante + MARGEM_S) * 1000);
  }

  function pausarCronometro() {
    limparTimer();
    if (playStartRef.current !== null) {
      acumuladoRef.current += (Date.now() - playStartRef.current) / 1000;
      playStartRef.current  = null;
    }
  }

  function iniciarBufWatchdog() {
    if (!hasPlayedOnceRef.current) return;
    limparBufWatchdog();
    bufWatchdogRef.current = setTimeout(() => {
      if (pausadoPorApiRef.current || destroyedRef.current) return;
      try { playerRef.current?.playVideo?.(); } catch { /* ignore */ }
      bufWatchdogRef.current = setTimeout(() => {
        if (endedCalledRef.current || destroyedRef.current || pausadoPorApiRef.current) return;
        if (modoLiveRef.current) {
          console.warn('[YoutubeSlide] Freeze em live — tentando retomar:', videoIdRef.current);
          try { playerRef.current?.playVideo?.(); } catch { /* ignore */ }
          iniciarBufWatchdog();
          return;
        }
        console.warn('[YoutubeSlide] Freeze mid-playback — pulando:', videoIdRef.current);
        dispararFim();
      }, 5_000);
    }, 25_000);
  }

  function resetParaNovoVideo() {
    endedCalledRef.current   = false;
    acumuladoRef.current     = 0;
    playStartRef.current     = null;
    hasPlayedOnceRef.current = false;
    pausadoPorApiRef.current = false;
    limparTimer();
    limparBufWatchdog();
  }

  /** Republica o vídeo no segundo atual. Devolve false se o player ainda não está pronto. */
  function reloadAtCurrentTime(): boolean {
    const player = playerRef.current;
    if (!player || !playerReadyRef.current || destroyedRef.current) return false;
    pausadoPorApiRef.current = false;
    if (modoLiveRef.current) {
      resetParaNovoVideo();
      player.loadVideoById?.(videoIdRef.current);
      desativarLegendas(player);
      return true;
    }
    let seekTo = 0;
    try { seekTo = Math.max(0, player.getCurrentTime?.() ?? 0); } catch { /* ignore */ }
    endedCalledRef.current   = false;
    playStartRef.current     = null;
    hasPlayedOnceRef.current = false;
    acumuladoRef.current     = seekTo;
    limparTimer();
    limparBufWatchdog();
    player.loadVideoById?.(videoIdRef.current, seekTo);
    desativarLegendas(player);
    return true;
  }

  // ── useImperativeHandle ───────────────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    play() {
      // Com controls=0, um pauseVideo() por API deixa o ytp-bezel de pause na
      // tela e o playVideo() seguinte NÃO o limpa — o vídeo volta a tocar com o
      // ícone por cima. Só loadVideoById reconstrói o chrome do embed, então a
      // retomada após pause por API republica no segundo atual.
      if (pausadoPorApiRef.current && reloadAtCurrentTime()) return;
      pausadoPorApiRef.current = false;
      playerRef.current?.playVideo?.();
    },
    pause() {
      pausadoPorApiRef.current = true;
      playerRef.current?.pauseVideo?.();
    },
    mute()   { playerRef.current?.mute?.(); },
    unMute() { playerRef.current?.unMute?.(); },
    setVolume(volume: number) { playerRef.current?.setVolume?.(volume); },

    recoverFromNetworkDrop() {
      if (pausadoPorApiRef.current) return;
      const player = playerRef.current;
      if (!player || !playerReadyRef.current || destroyedRef.current) return;
      let seekTo = 0;
      try { seekTo = Math.max(0, (player.getCurrentTime?.() ?? 0) - 2); } catch { /* ignore */ }
      resetParaNovoVideo();
      player.loadVideoById?.(videoIdRef.current, seekTo);
      desativarLegendas(player);
    },

    reloadAtCurrentTime() { reloadAtCurrentTime(); },
  }));

  // ── Fullscreen / visibilidade ─────────────────────────────────────────────
  // Pausa intencional (Controle Remoto, Momento Especial) não é retomada aqui:
  // playVideo() após pause por API deixa o ytp-bezel preso, e a faixa podia
  // terminar escondida durante o Momento Especial (avançando a fila).
  useEffect(() => {
    function onFullscreenChange() {
      fullscreenGraceRef.current = true;
      if (fullscreenTimerRef.current) clearTimeout(fullscreenTimerRef.current);
      fullscreenTimerRef.current = setTimeout(() => {
        fullscreenGraceRef.current = false;
        if (pausadoPorApiRef.current) return;
        if (!modoLiveRef.current) iniciarTimerPreciso();
        try { playerRef.current?.playVideo?.(); } catch { /* ignore */ }
      }, 3_000);
    }
    function onVisibilityChange() {
      if (document.visibilityState !== 'visible' || pausadoPorApiRef.current) return;
      try { playerRef.current?.playVideo?.(); } catch { /* ignore */ }
    }
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (fullscreenTimerRef.current) clearTimeout(fullscreenTimerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Mount: cria o YT.Player UMA VEZ ──────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    destroyedRef.current = false;
    const initialVideoId = videoIdRef.current;

    function onStateChange(e: YTPlayerEvent) {
      if (destroyedRef.current) return;
      const state = e.data;
      if (state === 0) {
        limparBufWatchdog();
        dispararFim();
      } else if (state === 1) {
        desativarLegendas(playerRef.current);
        hasPlayedOnceRef.current = true;
        limparBufWatchdog();
        pausarCronometro();
        playStartRef.current = Date.now();
        iniciarTimerPreciso();
      } else if (state === 3) {
        if (!fullscreenGraceRef.current) pausarCronometro();
        iniciarBufWatchdog();
      } else if (state === 2) {
        limparBufWatchdog();
        if (!fullscreenGraceRef.current) pausarCronometro();
      }
    }

    function onError(e: YTPlayerEvent) {
      console.warn(`[YoutubeSlide] Erro ${e.data} no vídeo:`, videoIdRef.current);
      if (!destroyedRef.current) onVideoErrorRef.current?.(e.data);
      dispararFim();
    }

    carregarYouTubeAPI().then(() => {
      if (destroyedRef.current || !containerRef.current || !window.YT?.Player) return;
      // Dimensões explícitas garantem que o ABR do YouTube parta do nível máximo de qualidade.
      // setPlaybackQuality() e vq estão depreciados desde 2026 — tamanho do player é o único lever.
      const w = containerRef.current.offsetWidth  || 1920;
      const h = containerRef.current.offsetHeight || 1080;
      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId: initialVideoId,
        width:   w,
        height:  h,
        playerVars: {
          autoplay:        1,
          controls:        0,
          modestbranding:  1,
          iv_load_policy:  3,
          cc_load_policy:  3,
          rel:             0,
          enablejsapi:     1,
          mute:            1,
          end:             0,
          origin:          window.location.origin,
        },
        events: {
          onReady(e) {
            if (destroyedRef.current) return;
            playerReadyRef.current = true;
            desativarLegendas(e.target);
            // Vídeo pode ter mudado enquanto a API carregava
            if (videoIdRef.current !== initialVideoId) {
              e.target.loadVideoById?.(videoIdRef.current);
            }
            if (unlockedRef.current) {
              if (initialMutedRef.current) {
                e.target.mute?.();
              } else {
                e.target.unMute?.();
                e.target.setVolume?.(initialVolumeRef.current);
              }
            }
            e.target.playVideo?.();
          },
          onStateChange,
          onError,
          onPlaybackQualityChange(e) {
            if (!destroyedRef.current) {
              console.info('[YoutubeSlide] quality:', e.target.getPlaybackQuality?.() ?? e.data, 'videoId:', videoIdRef.current);
            }
          },
        },
      });
    });

    return () => {
      destroyedRef.current   = true;
      playerReadyRef.current = false;
      limparTimer();
      limparBufWatchdog();
      try { playerRef.current?.destroy?.(); } catch { /* ignore */ }
      playerRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Troca de vídeo: loadVideoById em vez de recriar o player ─────────────
  useEffect(() => {
    if (isFirstVideoRef.current) {
      isFirstVideoRef.current = false;
      return;
    }
    resetParaNovoVideo();
    playerRef.current?.loadVideoById?.(videoId);
    desativarLegendas(playerRef.current);
  }, [videoId, playKey]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleUnlockClick() {
    if (initialMutedRef.current) {
      playerRef.current?.mute?.();
    } else {
      playerRef.current?.unMute?.();
      playerRef.current?.setVolume?.(initialVolumeRef.current);
    }
    playerRef.current?.playVideo?.();
    onUnlock?.();
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="w-full h-full bg-black relative">
      <div
        ref={containerRef}
        title={titulo}
        style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }}
      />
      {unlocked ? (
        <div className="absolute inset-0 cursor-none" />
      ) : (
        <div
          role="button"
          tabIndex={0}
          aria-label="Toque para iniciar reprodução com som"
          className="absolute inset-0 flex items-center justify-center backdrop-blur-md bg-black/30 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-inset"
          onClick={handleUnlockClick}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            handleUnlockClick();
          }}
        >
          <div className="flex flex-col items-center gap-4 select-none">
            <div className="w-24 h-24 rounded-full bg-white/15 backdrop-blur-sm border border-white/30 flex items-center justify-center shadow-2xl hover:bg-white/25 transition-colors">
              <svg className="w-10 h-10 text-white ml-1.5 drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
            <p className="text-white/80 text-sm font-medium tracking-wide drop-shadow">Toque para iniciar com som</p>
          </div>
        </div>
      )}
    </div>
  );
});

export default YoutubeSlide;
